import { RETURN_ELIGIBILITY_MESSAGES } from '@/constants/appReturn.const'
// import { CUSTOMER_RETURN_CONFIG } from '@/config/customerReturn.config'

/**
 * Customer Return Validation Composable
 *
 * Provides validation functions for the customer return flow including
 * item returnability checks, quantity validation, and processing readiness.
 */

/**
 * @typedef {Object} OrderLineItem
 * @property {number} quantityAvailableToReturn - Quantity available for return
 * @property {boolean} isReturnable - Whether item is returnable
 * @property {boolean} [selected] - Whether item is selected for return
 * @property {number} [selectedQuantity] - Quantity selected for return
 */

/**
 * Composable for customer return validation logic
 * @returns {Object} Validation functions
 */
export function useCustomerReturnValidation() {
  /**
   * Check if an item can be returned
   * @param {OrderLineItem} item - The order line item to check
   * @returns {boolean} True if item can be returned
   */
  function isItemReturnable(item) {
    if (!item) return false
    return item.quantityAvailableToReturn > 0 && item.isReturnable !== false
  }

  /**
   * Get the reason an item cannot be returned
   * @param {OrderLineItem} item - The order line item to check
   * @param {boolean} returnPolicyOk - Whether return policy allows returns
   * @returns {string|null} Message explaining why item can't be returned, or null if returnable
   */
  function getReturnBlockingReason(item, returnPolicyOk) {
    if (!item) return null

    // Check if item is marked as final sale
    if (item.isReturnable === false) {
      return RETURN_ELIGIBILITY_MESSAGES.FINAL_SALE
    }

    // Check if return policy prevents returns
    // We also check fulfillments because if there are none, we don't know shipment status
    if (returnPolicyOk === false && Array.isArray(item.returnableFulfillments) && item.returnableFulfillments.length > 0) {
      return RETURN_ELIGIBILITY_MESSAGES.OUTSIDE_POLICY
    }

    // Check if no quantity available
    if (item.quantityAvailableToReturn === 0) {
      return 'No quantity available for return'
    }

    return null
  }

  /**
   * Validate that at least one item is selected and ready for processing
   * @param {OrderLineItem[]} items - Array of order line items
   * @returns {boolean} True if can proceed to processing
   */
  function canProceedToProcessing(items) {
    if (!Array.isArray(items) || items.length === 0) {
      return false
    }

    // Check if at least one item is selected with quantity > 0
    return items.some(item => item.selected === true && item.selectedQuantity > 0)
  }

  /**
   * Validate array index is valid and within bounds
   * @param {number} index - Index to validate
   * @param {Array} itemsArray - Array to check against
   * @returns {boolean} True if index is valid
   */
  function isValidItemIndex(index, itemsArray) {
    if (typeof index !== 'number' || index < 0) {
      return false
    }

    if (!Array.isArray(itemsArray)) {
      return false
    }

    return index < itemsArray.length
  }

  /**
   * Validate selected quantity for an item
   * @param {OrderLineItem} item - The item to validate
   * @param {number} quantity - The quantity to validate
   * @returns {boolean} True if quantity is valid
   */
  function isValidQuantity(item, quantity) {
    if (!item || typeof quantity !== 'number') {
      return false
    }

    return quantity > 0 && quantity <= item.quantityAvailableToReturn
  }

  /**
   * Calculate returnable quantity for an item
   * @param {OrderLineItem} item - The item to check
   * @param {boolean} returnPolicyOk - Whether return policy allows returns
   * @returns {number} The quantity available to return (0 if not returnable)
   */
  function getReturnableQuantity(item, returnPolicyOk) {
    if (!item) return 0

    // If final sale or policy blocks, return 0
    if (item.isReturnable === false || returnPolicyOk === false) {
      return 0
    }

    return item.quantityAvailableToReturn || 0
  }

  /**
   * Validate that selected items have all required data for processing
   * @param {OrderLineItem[]} items - Array of selected items
   * @returns {{ valid: boolean, errors: string[] }} Validation result
   */
  function validateItemsForProcessing(items) {
    const errors = []

    if (!Array.isArray(items)) {
      errors.push('Items must be an array')
      return { valid: false, errors }
    }

    if (items.length === 0) {
      errors.push('No items selected for return')
      return { valid: false, errors }
    }

    items.forEach((item, index) => {
      if (!item.platformOrderLineItemId) {
        errors.push(`Item ${index + 1}: Missing platformOrderLineItemId`)
      }

      if (!item.selectedQuantity || item.selectedQuantity <= 0) {
        errors.push(`Item ${index + 1}: Invalid quantity`)
      }
    })

    return {
      valid: errors.length === 0,
      errors,
    }
  }

  return {
    canProceedToProcessing,
    getReturnableQuantity,
    getReturnBlockingReason,
    isItemReturnable,
    isValidItemIndex,
    isValidQuantity,
    validateItemsForProcessing,
  }
}
